import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import './App.css';

// PrimeReact imports
import 'primereact/resources/themes/lara-light-green/theme.css';  // green theme to match logo
import 'primereact/resources/primereact.min.css';                  // core css
import 'primeicons/primeicons.css';                               // icons
import 'primeflex/primeflex.css';                                 // PrimeFlex for responsive grid system

// Custom CSS
import './assets/custom.css';                                     // Custom CSS fixes and enhancements
import './assets/darkMode.css';                                   // Dark mode styles
import './assets/darkMode-extra.css';                             // Additional dark mode fixes

// Optimizations
import './utils/optimizations';
import { setupDesktopReveal } from './utils/immediateReveal';     // Import immediate reveal utility

// Layout components
import MainLayout from './layouts/MainLayout';
import SplashScreen from './components/SplashScreen';
import PageTransition from './components/PageTransition';

// import SmartTraffic from './pages/Industries/SmartTraffic';

// Loading fallback
const PageLoading = () => (
  <div className="flex justify-content-center align-items-center" style={{ height: '70vh' }}>
    <div className="flex flex-column align-items-center">
      <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
      <p>Loading page...</p>
    </div>
  </div>
);

// Lazy load pages for better performance
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const Contact = lazy(() => import('./pages/Contact'));
const XConnectedAI = lazy(() => import('./pages/XConnectedAI'));

const MLFF = lazy(() => import('./pages/Solutions/MLFF'));
const QSR = lazy(() => import('./pages/Solutions/qsr'));
const ITMS = lazy(() => import('./pages/Solutions/itms'));
const VMS = lazy(() => import('./pages/Solutions/vms'));
const NMS = lazy(() => import('./pages/Solutions/nms'));
const EMS = lazy(() => import('./pages/Solutions/ems'));
const CrowdManagementSolution = lazy(() => import('./pages/Solutions/CrowdManagementSolution'));
const Blockchain = lazy(() => import('./pages/Solutions/blockchain'));
const TrafficHotspots = lazy(() => import('./pages/Solutions/TrafficHotspots'));
const AutomatedInvoiceProcessingSystem = lazy(() => import('./pages/Solutions/AutomatedInvoiceProcessingSystem'));
const PublicAnnouncementSystem = lazy(() => import('./pages/Solutions/PublicAnnouncementSystem'));
const MultilingualAnprSolution = lazy(() => import('./pages/Solutions/MultilingualAnprSolution'));

const AiMlTraining = lazy(() => import('./pages/Solutions/AiMlTraining'));
const SmartCity = lazy(() => import('./pages/Industries/SmartCity'));
const SmartTraffic1 = lazy(() => import('./pages/Industries/SmartTraffic1'));
const SmartFactory = lazy(() => import('./pages/Industries/SmartFactory'));
const SmartRestaurant1 = lazy(() => import('./pages/Industries/SmartRestaurant'));
const RealEstate1 = lazy(() => import('./pages/Industries/RealEstate1'));
const PremiseSafety = lazy(() => import('./pages/Industries/PremiseSafety'));
const Defense = lazy(() => import('./pages/Industries/defense'));
const Agriculture = lazy(() => import('./pages/Industries/Agriculture'));
const CSR = lazy(() => import('./pages/Portfolio/CSR'));
const Download = lazy(() => import('./pages/Portfolio/download'));
const NotFound = lazy(() => import('./pages/NotFound'));

function App() {
  const [showSplash, setShowSplash] = useState(true);
  
  // Check if the user has already seen the splash screen in this session
  useEffect(() => {
    const hasSeen = sessionStorage.getItem('hasSeen');
    
    // You can comment out this condition during development to always see the splash screen
    if (hasSeen === 'true') {
      setShowSplash(false);
    }
    
    // Setup immediate reveal for desktop to prevent fading effect
    setupDesktopReveal();
  }, []);

  const handleSplashFinish = () => {
    setShowSplash(false);
    sessionStorage.setItem('hasSeen', 'true');
    
    // After splash screen, apply immediate reveal again
    if (window.innerWidth >= 768) {
      setTimeout(() => {
        document.querySelectorAll('.reveal-bottom, .reveal-left, .reveal-right')
          .forEach(el => el.classList.add('active'));
      }, 300);
    }
  };

  if (showSplash) {
    return <SplashScreen onFinish={handleSplashFinish} />;
  }

  return (
    <Router>
      <MainLayout>
        {/* We're wrapping Routes in our PageTransition component for smooth transitions */}
        <PageTransition>
          <Suspense fallback={<PageLoading />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/xconnected-ai" element={<XConnectedAI />} />
              <Route path="/download" element={<Download />} />
              <Route path="/csr" element={<CSR />} />
              <Route path="/mlff" element={<MLFF />} />
              <Route path="/qsr" element={<QSR />} />
              <Route path="/AutomatedInvoiceProcessingSystem" element={<AutomatedInvoiceProcessingSystem />} />
              <Route path="/public-announcement-system" element={<PublicAnnouncementSystem />} />
              <Route path="/multilingual-anpr-solution" element={<MultilingualAnprSolution />} />
              <Route path="/itms" element={<ITMS />} />
              <Route path="/vms" element={<VMS />} />
                <Route path="/ai-ml-training" element={<AiMlTraining />} />
              <Route path="/nms" element={<NMS />} />
               <Route path="/ems" element={<EMS />} />
                <Route path="/CrowdManagementSolution" element={<CrowdManagementSolution />} />
                <Route path="/blockchain" element={<Blockchain />} />
                <Route path="/TrafficHotspots" element={<TrafficHotspots />} />
                
              <Route path="/smart-city" element={<SmartCity />} />
              <Route path="/smart-traffic" element={<SmartTraffic1 />} />
              <Route path="/smart-factory" element={<SmartFactory />} />
              <Route path="/smart-restaurant" element={<SmartRestaurant1 />} />
              <Route path="/real-estate" element={<RealEstate1 />} />
              <Route path="/premise-safety" element={<PremiseSafety />} />
              <Route path="/defense" element={<Defense />} />
              <Route path="/agriculture" element={<Agriculture />} />
              {/* 404 catch-all route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </PageTransition>
      </MainLayout>
    </Router>
  );
}

export default App;
